/**
 * Modals for SmswiserCampaignStatsTab (Copy as draft, Scale)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { CopyAsDraftModal } from '@/features/smswiser-campaigns/components/modals';
import ScaleDropModal from '@/features/smswiser-campaigns/components/ScaleDropModal';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { UseQueryResult } from '@tanstack/react-query';
import type { LeadsList, ListPerformanceTagsResponse } from '@/hooks';

import type { useScaleDropHandlers } from '../hooks';

type ScaleDropHandlers = ReturnType<typeof useScaleDropHandlers>;

interface SmswiserCampaignStatsTabModalsProps {
  countryKey: string;
  workDate: string;
  titlePrefix: string;
  showCopyAsDraftModal: boolean;
  showScaleModal: boolean;
  selectedDropsForModals: DropsWithStatsRow[];
  wiserIds: number[];
  routes: Array<{ id: number; name: string; pricePerSms?: number | null }>;
  leadsLists: LeadsList[] | Array<{ id: number; name: string; count?: number | null }>;
  tlds: Array<{ value: string; displayName: string }>;
  senderNames: string[];
  smsTexts: string[];
  smsTextsWithIds?: Array<{ text: string; templateId: string }>;
  scaleHandlers: ScaleDropHandlers;
  perfTagsQuery?: UseQueryResult<ListPerformanceTagsResponse>;
  onCloseCopyAsDraft: () => void;
  onCloseScale: () => void;
}

export const SmswiserCampaignStatsTabModals: FC<SmswiserCampaignStatsTabModalsProps> = ({
  countryKey,
  workDate,
  titlePrefix,
  showCopyAsDraftModal,
  showScaleModal,
  selectedDropsForModals,
  wiserIds,
  routes,
  leadsLists,
  tlds,
  senderNames,
  smsTexts,
  smsTextsWithIds,
  scaleHandlers,
  perfTagsQuery,
  onCloseCopyAsDraft,
  onCloseScale,
}) => {
  if (selectedDropsForModals.length === 0) return null;

  return (
    <>
      {showCopyAsDraftModal && (
        <CopyAsDraftModal
          rows={selectedDropsForModals}
          routes={routes}
          defaultDate={workDate}
          isBusy={scaleHandlers.createPrepared.isPending}
          onClose={onCloseCopyAsDraft}
          onConfirm={(targetWorkDate, gatewayOverrides, staticTagId) =>
            scaleHandlers.handleCopyAsDraft(
              selectedDropsForModals,
              targetWorkDate,
              gatewayOverrides,
              staticTagId,
            )
          }
        />
      )}

      {showScaleModal && (
        <ScaleDropModal
          countryKey={countryKey}
          rows={selectedDropsForModals}
          wiserIds={wiserIds}
          gateways={routes}
          leadsLists={leadsLists as LeadsList[]}
          perfTagsQuery={perfTagsQuery}
          tlds={tlds}
        senderNames={senderNames}
        smsTexts={smsTexts}
        smsTextsWithIds={smsTextsWithIds}
        defaultWorkDate={workDate}
          titlePrefix={titlePrefix}
          isBusy={
            scaleHandlers.createPrepared.isPending ||
            scaleHandlers.sendPrepared.isPending ||
            scaleHandlers.updateCreative.isPending ||
            scaleHandlers.deleteCampaignSender.isPending ||
            scaleHandlers.deleteCampaignText.isPending
          }
          onClose={onCloseScale}
          onAddSender={scaleHandlers.handleAddSender}
          onAddText={scaleHandlers.handleAddText}
          onDeleteSender={scaleHandlers.handleDeleteSender}
          onDeleteText={scaleHandlers.handleDeleteText}
          onDeleteTextsBulk={scaleHandlers.handleDeleteTextsBulk}
          onConfirm={scaleHandlers.handleScaleConfirm}
        />
      )}
    </>
  );
};
